class WebSocketService {
  private url: string;
  public socket: WebSocket | null; // Make socket public and explicitly typed
  private onFrameCallback: ((frameData: string) => void) | null;
                                                                                                                                                                                  private onPixelSpacingCallback: ((pixelSpacingData: any) => void) | null;
  private onErrorCallback: ((error: string) => void) | null;
  private onOpenCallback: (() => void) | null;
  private onCloseCallback: ((event: CloseEvent) => void) | null;                                                                                  
  private shouldReconnect: boolean;
  private hasShownError: boolean = false; // 🔐 new flag
  private debugLastSendLogMs: number = 0;
  private debugSendCount: number = 0;
  private debugSendOkCount: number = 0;
  private debugLastConnectLogMs: number = 0;

  constructor(url: string) {
    this.url = url;
    this.socket = null;
    this.onFrameCallback = null;
    this.onPixelSpacingCallback = null;
    this.onErrorCallback = null;
    this.onOpenCallback = null;
    this.onCloseCallback = null;
    this.shouldReconnect = true;
  }

  private closeSocketInternal(): void {
    if (!this.socket) return;
    try {
      // Remove listeners to prevent duplicate reconnection attempts
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
    } catch {}
    try {
      this.socket.close();
    } catch {}
    this.socket = null;
  }

  connect(): void {
    // Ensure auto-reconnect remains enabled for normal operation.
    // (disconnect() is reserved for explicit user-driven teardown.)
    this.shouldReconnect = true;

    // Close existing connection if one exists, without disabling reconnect.
    if (this.socket) this.closeSocketInternal();

    this.socket = new WebSocket(this.url);
    const now = Date.now();
    if (now - this.debugLastConnectLogMs > 1000) {
      console.log('[WS] connect() -> new WebSocket', { url: this.url });
      this.debugLastConnectLogMs = now;
    }

    this.socket.onopen = () => {
      console.log('[WS] open');
      this.onOpenCallback?.();
    };

    this.socket.onmessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);
        if (
          (message.type === 'processed_frame' || message.type === 'prediction_succeeded') && 
          this.onFrameCallback
        ) {
          this.onFrameCallback(JSON.stringify(message));
        }
        if (message.type === 'pixel_spacing_result' && this.onPixelSpacingCallback) {
          console.log('📏 Pixel spacing result received:', message);
          this.onPixelSpacingCallback(message);
        }
         if (message.type === 'error') {
          console.error('WebSocket Error Message:', message.message);
          if (this.onErrorCallback) {
            this.onErrorCallback(message.message);
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
        this.onErrorCallback?.('Failed to parse WebSocket message.')
      }
    };

    this.socket.onerror = (error: Event) => {
      console.error('WebSocket Error:', error);
      this.onErrorCallback?.('WebSocket connection error.');
    };

    this.socket.onclose = (event: CloseEvent) => {
      console.log('[WS] close', { code: event.code, reason: event.reason });
      
      // Notify close callback
      this.onCloseCallback?.(event);
      
      // Only show error for abnormal closures
      if (event.code === 1011) {
        this.onErrorCallback?.('Backend crashed unexpectedly.');
      } else if (event.code !== 1000 && event.code !== 1001 && this.onErrorCallback) {
        // 1000 = normal closure, 1001 = going away (page refresh/navigation)
        // Only show error for other close codes
        console.warn('WebSocket closed with code:', event.code);
      }
      
      if (this.shouldReconnect) {
        console.log('[WS] reconnect scheduled (2s)');
        setTimeout(() => this.connect(), 2000);
      }
    };
  }

  /**
   * Send a frame to the backend for inference (legacy base64 method).
   * @deprecated Use sendFrameBinary for better performance and pixel preservation
   */
  sendFrame(
    frameData: string, 
    pixelSpacing: string, 
    sessionId?: string,
    roiConfig?: { crop_x: number; crop_y: number; crop_width: number; crop_height: number } | null
  ): boolean {
    this.debugSendCount += 1;
    const rs = this.socket?.readyState ?? -1;
    // Throttle send logging (every ~2s)
    const now = Date.now();
    if (now - this.debugLastSendLogMs >= 2000) {
      console.log('[WS] sendFrame stats', {
        ok: this.debugSendOkCount,
        tries: this.debugSendCount,
        readyState: rs,
      });
      this.debugLastSendLogMs = now;
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message: any = {
        type: 'frame',
        data: frameData,
        pixelSpacing: pixelSpacing
      };
      if (sessionId) {
        message.session_id = sessionId;
      }
      // Include ROI config for server-side cropping (preserves pixel spacing)
      if (roiConfig) {
        message.roi = roiConfig;
      }
      try {
        this.socket.send(JSON.stringify(message));
        this.debugSendOkCount += 1;
        return true;
      } catch (error) {
        console.error('Failed to send frame:', error);
        this.onErrorCallback?.('Failed to send frame');
        return false;
      }
    } else {
      return false;
    }
  }

  /**
   * Send a frame as raw binary data for inference.
   * 
   * Binary protocol:
   * [4 bytes: metadata length (uint32 big-endian)]
   * [N bytes: JSON metadata]
   * [remaining bytes: raw image data]
   * 
   * This preserves exact pixel values (no base64 encoding overhead or artifacts).
   * 
   * @param imageBlob - Raw image blob (PNG/JPEG)
   * @param pixelSpacing - Pixel spacing value
   * @param contentType - MIME type (e.g., "image/png", "image/jpeg")
   * @param sessionId - Optional session ID for tracking
   * @param roiConfig - Optional ROI configuration for server-side cropping
   */
  async sendFrameBinary(
    imageBlob: Blob,
    pixelSpacing: string,
    contentType: string,
    sessionId?: string,
    roiConfig?: { crop_x: number; crop_y: number; crop_width: number; crop_height: number } | null
  ): Promise<boolean> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      const metadata: any = {
        type: 'binary_frame',
        pixelSpacing: pixelSpacing,
        content_type: contentType
      };
      // if (sessionId) {
      //   metadata.session_id = sessionId;
      // }
      if (roiConfig) {
        metadata.roi = roiConfig;
      }

      const metadataJson = JSON.stringify(metadata);
      const metadataBytes = new TextEncoder().encode(metadataJson);
      
      const imageArrayBuffer = await imageBlob.arrayBuffer();
      const imageBytes = new Uint8Array(imageArrayBuffer);

      const totalLength = 4 + metadataBytes.length + imageBytes.length;
      const binaryMessage = new ArrayBuffer(totalLength);
      const view = new DataView(binaryMessage);
      const uint8View = new Uint8Array(binaryMessage);

      view.setUint32(0, metadataBytes.length, false);

      uint8View.set(metadataBytes, 4);

      uint8View.set(imageBytes, 4 + metadataBytes.length);

      this.socket.send(binaryMessage);
      return true;
    } catch (error) {
      console.error('Failed to send binary frame:', error);
      this.onErrorCallback?.('Failed to send binary frame');
      return false;
    }
  }

  setOnFrameCallback(callback: ((frameData: string) => void) | null): void {
    this.onFrameCallback = callback;
  }

  setOnPixelSpacingCallback(callback: ((pixelSpacingData: any) => void) | null): void {
    this.onPixelSpacingCallback = callback;
  }

  setOnOpenCallback(callback: (() => void) | null): void {
    this.onOpenCallback = callback;
  }

  setOnCloseCallback(callback: ((event: CloseEvent) => void) | null): void {
    this.onCloseCallback = callback;
  }

  setOnErrorCallback(callback: (error: string) => void): void {
    this.onErrorCallback = (error: string) => {
      if (!this.hasShownError) {
        this.hasShownError = true; // ✅ prevent duplicate error showing
        callback(error);
      } else {
        console.warn("Duplicate WebSocket error suppressed:", error);
      }
    };
  }

  resetErrorState(): void {
    this.hasShownError = false; // 🔁 allow error to be shown again when needed
  }

  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  disconnect(): void {
    this.shouldReconnect = false;
    
    if (this.socket) {
      this.closeSocketInternal();
    }
    
    console.log('WebSocket Disconnected Manually');
  }
}

export default WebSocketService;
